import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import AgGridTable from '../../components/AgGridTable';
import ReferralDetailPanel from '../../components/referral-management/ReferralDetailPanel';
import CreateReferralModal from '../../components/referral-management/CreateReferralModal';
import AlertMessage from '../../components/shared/alertMessage';
import VCXReferralFilterBar from '../../components/shared/VCXReferralFilterBar';
import {
  setReferralTypes,
  setReferralStatuses,
  setSelectedReferral,
  setLoading,
  setError,
  clearError,
  setDetailPanelOpen,
  setPagination,
  setPaginatedReferralsData,
  setFilters,
  selectReferralsData,
  selectSelectedReferral,
  selectLoading,
  selectError,
  selectPagination,
  selectFilters,
} from '../../components/redux/slices/referral-management/referralSlice';
import { updateUserPreferenceKey, saveUserPreferences } from '../../components/redux/slices/userPreferencesSlice';
import { selectCurrentUser } from '../../components/redux/slices/userSlice';

const ReferralManagementPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { referralId } = useParams();

  const referralsData = useSelector(selectReferralsData);
  const selectedReferral = useSelector(selectSelectedReferral);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const pagination = useSelector(selectPagination);
  const reduxFilters = useSelector(selectFilters);
  const allUserPreferences = useSelector((state) => state.userPreferences.data);
  const currentUser = useSelector(selectCurrentUser);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [initialTab, setInitialTab] = useState('overview');
  const [fetchingReferralId, setFetchingReferralId] = useState(null);
  const [localFilters, setLocalFilters] = useState(reduxFilters);
  const [filtersModified, setFiltersModified] = useState(false);

  // Ensure date_field_type has a default value for backwards compatibility
  useEffect(() => {
    if (localFilters && !localFilters.date_field_type) {
      setLocalFilters(prev => ({ ...prev, date_field_type: 'created' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter configuration for VCXReferralFilterBar
  const filterConfig = useMemo(() => [
    {
      type: 'select',
      name: 'urgency_level',
      options: [
        { value: '', label: 'All Urgencies' },
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' }
      ],
      width: 'auto'
    },
    {
      type: 'select',
      name: 'is_complete',
      placeholder: 'Status',
      options: [
        { value: '', label: 'All Statuses' },
        { value: 'false', label: 'In Progress' },
        { value: 'true', label: 'Complete' }
      ],
      width: 'auto'
    },
    {
      type: 'daterange',
      name: 'date_range',
      placeholder: 'Date Range',
      width: '250px'
    },
    {
      type: 'select',
      name: 'ehr_completion',
      placeholder: 'EHR Status',
      options: [
        { value: '', label: 'All EHR Statuses' },
        { value: 'pending', label: 'EHR Pending' },
        { value: 'completed', label: 'EHR Completed' }
      ],
      width: 'auto'
    },
    {
      type: 'text',
      name: 'search',
      placeholder: 'Search patient/case...',
      width: '200px'
    }
  ], []);

  // Fetch configuration data (types & statuses)
  // Pass health_system_id to get both global + health-system-specific types
  const fetchConfiguration = useCallback(async (healthSystemId = null) => {
    try {
      const params = { is_active: 'true' };
      if (healthSystemId) {
        params.health_system_id = healthSystemId;
      }

      const [typesResponse, statusesResponse] = await Promise.all([
        adminApi.get('admin/referral-types/', { params }),
        adminApi.get('admin/referral-statuses/', { params }),
      ]);

      if (typesResponse.data?.referral_types) {
        dispatch(setReferralTypes(typesResponse.data.referral_types));
      }
      if (statusesResponse.data?.referral_statuses) {
        dispatch(setReferralStatuses(statusesResponse.data.referral_statuses));
      }
    } catch (err) {
      console.error('Failed to fetch configuration:', err);
    }
  }, [dispatch]);

  // Fetch referrals
  const fetchReferrals = useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(clearError());

    try {
      const params = {
        ...reduxFilters,
        page: pagination.page,
        page_size: pagination.page_size,
      };

      // Clean up empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await adminApi.get('admin/referrals/', { params });

      if (response.data) {
        const paginationData = {
          page: response.data.page || 1,
          page_size: response.data.page_size || 50,
          total_count: response.data.count || 0,
          total_pages: Math.ceil((response.data.count || 0) / (response.data.page_size || 50)),
        };

        dispatch(setPaginatedReferralsData({
          referrals: response.data.results || response.data.referrals || [],
          pagination: paginationData,
        }));
      }
    } catch (err) {
      dispatch(setError(err.message || 'Failed to fetch referrals'));
      setAlertState({
        show: true,
        type: 'error',
        title: 'Failed to Load Referrals',
        message: err.message || 'An error occurred while fetching referrals'
      });
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, reduxFilters, pagination.page, pagination.page_size]);

  // Filter handlers
  const handleFilterChange = useCallback((name, value) => {
    setLocalFilters(prev => ({ ...prev, [name]: value }));
    setFiltersModified(true);
  }, []);

  const handleSearch = useCallback(() => {
    dispatch(setFilters(localFilters));
    dispatch(setPagination({ page: 1 }));
    setFiltersModified(false);
  }, [dispatch, localFilters]);

  const handleResetFilters = useCallback(() => {
    const defaultFilters = {
      is_active: 'true',
      is_complete: '',
      ehr_completion: '',
      urgency_level: '',
      search: '',
      assigned_to: '',
      date_field_type: 'created',
      from_date: '',
      to_date: '',
    };
    setLocalFilters(defaultFilters);
    dispatch(setFilters(defaultFilters));
    dispatch(setPagination({ page: 1 }));
    setFiltersModified(false);
  }, [dispatch]);

  const handleSaveAsDefault = useCallback(() => {
    const filtersToSave = { ...reduxFilters };
    delete filtersToSave.search;

    const updatedPreferences = {
      ...allUserPreferences,
      referral_management_filters: filtersToSave
    };

    dispatch(updateUserPreferenceKey({
      key: 'referral_management_filters',
      value: filtersToSave
    }));

    dispatch(saveUserPreferences(updatedPreferences)).then(() => {
      setAlertState({
        show: true,
        type: 'success',
        title: 'Filter Preferences Saved',
        message: 'Your default filter settings have been saved'
      });
      setFiltersModified(false);
    });
  }, [dispatch, reduxFilters, allUserPreferences]);

  // Fetch a specific referral by ID (for URL-based opening)
  const fetchReferralById = useCallback(async (id) => {
    setFetchingReferralId(id);
    try {
      const response = await adminApi.get(`admin/referrals/${id}/`);
      if (response.data) {
        const referralData = response.data.referral || response.data;
        dispatch(setSelectedReferral(referralData));
        dispatch(setDetailPanelOpen(true));
        setSidebarOpen(true);

        // If this is a health system referral, refetch types/statuses for that health system
        if (referralData.health_system?.id) {
          await fetchConfiguration(referralData.health_system.id);
        }
      }
    } catch (err) {
      setAlertState({
        show: true,
        type: 'error',
        title: 'Failed to Load Referral',
        message: err.message || 'Could not load the referral details'
      });
      navigate('/admin/referrals');
    } finally {
      setFetchingReferralId(null);
    }
  }, [dispatch, navigate, fetchConfiguration]);

  // Initial data fetch
  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  // Always read tab from URL whenever it changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab) {
      setInitialTab(tab);
    }
  }, [location.search]);

  // Handle URL-based panel opening
  useEffect(() => {
    if (referralId) {
      // If referral ID in URL doesn't match selected referral, open the correct one
      if (selectedReferral?.id !== referralId) {
        const existingReferral = referralsData.find(r => r.id === referralId);
        if (existingReferral) {
          dispatch(setSelectedReferral(existingReferral));
          dispatch(setDetailPanelOpen(true));
          setSidebarOpen(true);

          // If this is a health system referral, refetch types/statuses for that health system
          if (existingReferral.health_system?.id) {
            fetchConfiguration(existingReferral.health_system.id);
          }
        } else if (fetchingReferralId !== referralId) {
          // Fetch if not already fetching this specific referral
          fetchReferralById(referralId);
        }
      } else if (!sidebarOpen) {
        // Referral is already selected but panel is closed, open it
        dispatch(setDetailPanelOpen(true));
        setSidebarOpen(true);
      }
    } else if (!referralId && sidebarOpen) {
      // No referral ID in URL but panel is open, close it
      setSidebarOpen(false);
      dispatch(setDetailPanelOpen(false));
      dispatch(setSelectedReferral(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralId, referralsData, dispatch, fetchReferralById]);

  // Handle row click
  const handleRowClick = useCallback(async (params) => {
    const referral = params.data;
    dispatch(setSelectedReferral(referral));
    dispatch(setDetailPanelOpen(true));
    setSidebarOpen(true);

    // If this is a health system referral, refetch types/statuses for that health system
    if (referral.health_system?.id) {
      await fetchConfiguration(referral.health_system.id);
    }

    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab') || 'overview';
    navigate(`/admin/referrals/${referral.id}?tab=${tab}`);
  }, [dispatch, navigate, location.search, fetchConfiguration]);

  // Close detail panel
  const handleClosePanel = useCallback(() => {
    dispatch(setDetailPanelOpen(false));
    dispatch(setSelectedReferral(null));
    setSidebarOpen(false);
    setInitialTab('overview');
    navigate('/admin/referrals');
  }, [dispatch, navigate]);

  // Navigate between referrals
  const handleNavigateReferral = useCallback(async (direction) => {
    if (!selectedReferral || !referralsData.length) return;

    // Find current index
    const currentIndex = referralsData.findIndex(r => r.id === selectedReferral.id);
    if (currentIndex === -1) return;

    // Calculate new index
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex - 1;
    } else if (direction === 'next') {
      newIndex = currentIndex + 1;
    } else {
      return;
    }

    // Check bounds
    if (newIndex < 0 || newIndex >= referralsData.length) return;

    // Navigate to new referral
    const newReferral = referralsData[newIndex];
    dispatch(setSelectedReferral(newReferral));

    // If this is a health system referral, refetch types/statuses for that health system
    if (newReferral.health_system?.id) {
      await fetchConfiguration(newReferral.health_system.id);
    }

    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab') || 'overview';
    navigate(`/admin/referrals/${newReferral.id}?tab=${tab}`);
  }, [selectedReferral, referralsData, dispatch, navigate, location.search, fetchConfiguration]);

  // Pagination handlers
  const handlePageChange = useCallback((newPage) => {
    dispatch(setPagination({ page: newPage }));
  }, [dispatch]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    dispatch(setPagination({ page: 1, page_size: newPageSize }));
  }, [dispatch]);

  // Layout callbacks - memoized to prevent unnecessary re-renders
  const handleLayoutSaved = useCallback(() => {
    setAlertState({
      show: true,
      type: 'success',
      title: '',
      message: 'Table layout has been saved to your preferences'
    });
  }, []);

  const handleLayoutReset = useCallback(() => {
    setAlertState({
      show: true,
      type: 'info',
      title: '',
      message: 'Table layout has been reset to your saved preferences'
    });
  }, []);

  // Column definitions - memoized to prevent grid reinitialization
  const columnDefs = useMemo(() => [
    {
      headerName: 'Patient',
      field: 'patient_name',
      sortable: true,
      filter: true,
      width: 200,
      autoHeight: true,
      wrapText: true,
      cellStyle: { lineHeight: '1.5', padding: '8px' },
      cellRenderer: (params) => {
        const name = params.value || 'Unknown';
        const dob = params.data.patient_dob ? new Date(params.data.patient_dob).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '';
        const mrn = params.data.patient_mrn || '';
        return (
          <div>
            <div className="fw-semibold">{name}</div>
            {(dob || mrn) && (
              <small className="text-muted">
                {mrn && <span>MRN: {mrn}</span>}
                {mrn && dob && <span> | </span>}
                {dob && <span>DOB: {dob}</span>}
              </small>
            )}
          </div>
        );
      },
    },
    {
      headerName: 'Case/Encounter Type',
      field: 'case_name',
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: 'Assigned To',
      field: 'assigned_to_name',
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: 'Tasks',
      field: 'total_items',
      sortable: true,
      width: 100,
      cellRenderer: (params) => {
        const total = params.data.total_items || 0;
        const completed = params.data.completed_items || 0;
        const pending = params.data.pending_items || 0;
        return (
          <div>
            <span className="fw-bold">{total}</span>
            <span className="text-muted ms-2">
              ({completed} done, {pending} pending)
            </span>
          </div>
        );
      },
    },
    {
      headerName: 'Urgency',
      field: 'urgency_level',
      sortable: true,
      filter: true,
      width: 120,
      cellRenderer: (params) => {
        const urgency = params.value || 'medium';
        const colors = {
          low: 'success',
          medium: 'warning',
          high: 'warning text-black',
          critical: 'danger',
        };
        return (
          <span className={`badge bg-${colors[urgency] || colors.medium}`}>
            {urgency.toUpperCase()}
          </span>
        );
      },
    },
    {
      headerName: 'Deadline',
      field: 'overall_deadline',
      sortable: true,
      width: 150,
      cellStyle: { lineHeight: '1.5', padding: '8px' },
      cellRenderer: (params) => {
        if (!params.value) return '-';
        const deadline = new Date(params.value);
        const now = new Date();
        const daysUntil = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
        const isOverdue = daysUntil < 0;
        const isUrgent = daysUntil <= 3 && daysUntil >= 0;

        return (
          <div>
            <div className={isOverdue ? 'text-danger fw-bold' : isUrgent ? 'text-warning fw-bold' : ''}>
              {deadline.toLocaleDateString()}
            </div>
            {isOverdue && <small className="text-danger">OVERDUE</small>}
            {isUrgent && !isOverdue && <small className="text-warning">{daysUntil} day(s) left</small>}
          </div>
        );
      },
    },
    {
      headerName: 'Status',
      field: 'is_complete',
      sortable: true,
      filter: true,
      width: 120,
      cellRenderer: (params) => {
        const isComplete = params.value;
        return (
          <span className={`badge bg-${isComplete ? 'success' : 'primary'}`}>
            {isComplete ? 'COMPLETE' : 'IN PROGRESS'}
          </span>
        );
      },
    },
    {
      headerName: 'EHR Status',
      field: 'ehr_completion',
      sortable: true,
      filter: true,
      width: 180,
      autoHeight: true,
      wrapText: true,
      cellStyle: { lineHeight: '1.2', padding: '8px' },
      cellRenderer: (params) => {
        const ehrStatus = params.value || 'pending';
        const ehrCompleted = ehrStatus === 'completed';
        const completedAt = params.data.ehr_completed_at;
        const completedBy = params.data.ehr_completed_by_name;

        return (
          <div>
            <span className={`badge bg-${ehrCompleted ? 'success' : 'secondary'}`}>
              {ehrCompleted ? 'COMPLETED' : 'PENDING'}
            </span>
            {ehrCompleted && completedAt && (
              <small className="text-muted d-block mt-1">
                {new Date(completedAt).toLocaleDateString()}
              </small>
            )}
            {ehrCompleted && completedBy && (
              <small className="text-muted d-block">
                by {completedBy}
              </small>
            )}
          </div>
        );
      },
    },
    {
      headerName: 'Last Contacted',
      field: 'last_contacted_date',
      sortable: true,
      width: 280,
      autoHeight: true,
      wrapText: true,
      cellStyle: { lineHeight: '1.2', padding: '8px' },
      valueFormatter: (params) => {
        if (!params.value) return 'Never';
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
      cellRenderer: (params) => {
        if (!params.value) return <span className="text-muted">Never</span>;

        const date = new Date(params.value);
        const now = new Date();
        const daysAgo = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        const isRecent = daysAgo <= 3;
        const isStale = daysAgo > 7;
        const taskInfo = params.data.last_contacted_task_info;

        // Map call outcome to badge colors
        const outcomeColors = {
          'voicemail_left': 'warning',
          'transfer_failure': 'danger',
          'transferred': 'success',
          'transfer_success': 'success',
          'in_progress': 'primary',
          'no_answer': 'secondary',
          'patient_hangup': 'secondary',
          'voicemail': 'warning',
          'call_failed': 'danger',
        };

        const outcomeSlug = taskInfo?.last_call_outcome || '';
        const badgeColor = outcomeColors[outcomeSlug] || 'secondary';

        return (
          <div>
            <div className={`fw-semibold ${isStale ? 'text-warning' : isRecent ? 'text-success' : ''}`}>
              {date.toLocaleDateString()}
            </div>
            {taskInfo?.last_call_outcome_display && (
              <div className="mt-1">
                <span className={`badge bg-${badgeColor}`} style={{ fontSize: '0.7rem' }}>
                  {taskInfo.last_call_outcome_display}
                </span>
              </div>
            )}
            {taskInfo?.facility_name ? (
              <small className="text-muted d-block mt-1">
                <i className="fa fa-building me-1"></i>
                {taskInfo.facility_name}
              </small>
            ) : (
              taskInfo?.task_type && (
                <small className="text-muted d-block mt-1">
                  {taskInfo.task_type}
                </small>
              )
            )}
          </div>
        );
      },
    },
    {
      headerName: 'Gina Calls',
      field: 'gina_calls_count',
      sortable: true,
      width: 110,
      cellRenderer: (params) => {
        const count = params.value || 0;
        return (
          <span className={`badge bg-${count > 0 ? 'info' : 'secondary'}`}>
            {count} {count === 1 ? 'call' : 'calls'}
          </span>
        );
      },
    },
    {
      headerName: 'Created',
      field: 'created_at',
      sortable: true,
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      headerName: 'Organization',
      sortable: true,
      filter: true,
      width: 180,
      valueGetter: (params) => {
        return params.data.organization_name || params.data.health_system_name || '';
      },
    },
  ], []);

  // Default column definition - memoized to prevent grid reinitialization
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
  }), []);

  // Row style - memoized to prevent grid reinitialization
  const rowStyle = useMemo(() => ({ cursor: 'pointer' }), []);

  return (
    <div className="container-fluid mt-4">
      {/* Page Title */}
      <div className="page-title-box d-flex align-items-center justify-content-between">
        <div className="col-8">
          <h4 className="text-lg">Referral Management</h4>
          <p className="text-muted">Track medical clearances, vendor onboarding, and multi-step workflows</p>
        </div>
        <div className="col-4 d-flex align-items-center justify-content-end">
          {/* <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin/">Admin</a></li>
              <li className="breadcrumb-item active">Referral Management</li>
            </ol>
          </nav> */}
          <button
            className="btn btn-primary me-2"
            onClick={() => setCreateModalOpen(true)}
          >
            <i className="fa fa-plus me-1"></i>
            New Referral
          </button>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/admin/referrals/settings')}>
            <i className="fa fa-cog me-2"></i>Settings
          </button>
        </div>
      </div>

      {/* Alert */}
      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => dispatch(clearError())}
          ></button>
        </div>
      )}

      {/* Card Container */}
      <div className="card">
        {/* Filters */}
        <VCXReferralFilterBar
          filters={{ ...localFilters, hasChanges: filtersModified }}
          appliedFilters={reduxFilters}
          filterConfig={filterConfig}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleResetFilters}
          onSaveAsDefault={handleSaveAsDefault}
          loading={loading}
          searchButtonText="Apply Filters"
          showClearButton={true}
          showSaveButton={true}
          showAssignedToMe={true}
          currentUserId={currentUser?.djangoUserId}
          containerClassName="border-bottom"
        />

        {/* Actions 
        <div className="d-flex justify-content-end p-3 border-bottom">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => setCreateModalOpen(true)}
          >
            <i className="fa fa-plus me-1"></i>
            New Referral
          </button>
        </div> */}

        {/* Table */}
        <AgGridTable
          rowData={referralsData}
          columnDefs={columnDefs}
          loading={loading}
          defaultColDef={defaultColDef}
          gridOptions={{
            rowHeight: 60,
          }}
          suppressPaginationPanel={true}
          enableLayoutSaving={true}
          gridId="referral_management_grid"
          showLayoutControls={true}
          controlsPosition="top"
          onLayoutSaved={handleLayoutSaved}
          onLayoutReset={handleLayoutReset}
          onCellClicked={(params) => {
            // Don't open detail panel if clicking on Actions column
            const colDef = params.column?.getColDef();
            if (!colDef?.field) {
              return;
            }
            handleRowClick(params);
          }}
          rowStyle={rowStyle}
        />

        {/* Custom Pagination Controls */}
        {pagination.total_count > 0 && (
          <div className="d-flex justify-content-between align-items-center p-3 border-top">
            <div className="text-muted">
              Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total_count)} of {pagination.total_count} referrals
            </div>

            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <label className="text-muted mb-0">Rows per page:</label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: '80px' }}
                  value={pagination.page_size}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">
                  Page {pagination.page} of {pagination.total_pages || 1}
                </span>
                <div className="btn-group btn-group-sm">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1 || loading}
                    title="Previous page"
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= (pagination.total_pages || 1) || loading}
                    title="Next page"
                  >
                    <i className="fa fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail Panel Sidebar */}
      {sidebarOpen && selectedReferral && (
        <ReferralDetailPanel
          referral={selectedReferral}
          onClose={handleClosePanel}
          onUpdate={fetchReferrals}
          onNavigate={handleNavigateReferral}
          currentIndex={referralsData.findIndex(r => r.id === selectedReferral.id)}
          totalEvents={referralsData.length}
          initialTab={initialTab}
        />
      )}

      {/* Create Referral Modal */}
      <CreateReferralModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(newReferral) => {
          fetchReferrals();
          setAlertState({
            show: true,
            type: 'success',
            title: 'Referral Created',
            message: `Successfully created referral for ${newReferral.patient_name || 'patient'}`
          });
        }}
      />
    </div>
  );
};

export default ReferralManagementPage;
